import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import MuiLink from "@mui/material/Link";
import { BsLockFill } from "react-icons/bs";
import StevenGameInfo from "../StevenGameInfo";
import StevenButton from "../Common/StevenButton";
import { MOBILE_TAB_BAR_HEIGHT } from "../MobileTabBar";
import ResultIcon from "../Common/ResultIcon";
import { colors } from "../../theme";
import { getPickChanges } from "../../utils/pickChanges";
import gameService from "../../services/gameService";
import pickService from "../../services/pickService";
import apiClient from "../../services/apiClient";
import publicBettingService from "../../services/publicBettingService";
import featureAccessService from "../../services/featureAccessService";

// Formats a numeric spread/total value with a leading "+" for positives,
// matching how spreads are displayed elsewhere (e.g. StevenGameInfo).
const formatLineValue = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return val;
    return n > 0 ? `+${n}` : `${n}`;
};

const StevenGameList = ({ tempPicks,
    setMessage,
    gameStarted,
    setTempPicks,
    setError,
    user,
    donationAmount,
    selectedPicks,
    setSelectedPicks,
    getCommenceTimeByGameId,
    games,
    setGames
}) => {
    const [selectedGameId, setSelectedGameId] = useState()
    const [selectedGame, setSelectedGame] = useState(null)
    const [showStevenInfo, setShowStevenInfo] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [records, setRecords] = useState({})
    const [liveScores, setLiveScores] = useState({})
    const [publicBetting, setPublicBetting] = useState([])
    const [publicBettingThreshold, setPublicBettingThreshold] = useState(null)
    const isAdmin = user?.email?.toLowerCase() === "giulian.trabucco@gmail.com";
    const canViewPublicBetting = isAdmin || (publicBettingThreshold != null && donationAmount >= publicBettingThreshold);

    useEffect(() => {
        featureAccessService.getSettings().then(s => {
            setPublicBettingThreshold(s.publicBettingThreshold);
        });
    }, []);

    const fetchLiveScores = async () => {
        try {
            const res = await gameService.getLiveScores();
            const scoreMap = {};
            for (const game of (res.data?.games ?? [])) {
                // Key by team names so it works regardless of gameId format
                scoreMap[`${game.away_team}@${game.home_team}`] = game;
            }
            setLiveScores(scoreMap);
        } catch (_) { }
    };

    const fetchRecords = async () => {
        try {
            const res = await apiClient.get("/api/records");
            setRecords(res.data || {});
        } catch (_) { }
    };
    useEffect(() => {
        const fetchGames = async () => {
            try {
                const response = await gameService.getGames();
                setGames(response.data);
            } catch (error) {
                setError('Error fetching games');
            }
        };

        fetchGames();
        fetchPicks();
        fetchRecords();
        fetchLiveScores();

        // Refresh lines every 2 minutes (hits your own DB, not ESPN directly)
        const gamesInterval = setInterval(fetchGames, 2 * 60 * 1000);
        return () => clearInterval(gamesInterval);
    }, [])

    // Only poll live scores when games are actively in progress (started within last 4 hours)
    useEffect(() => {
        const anyInProgress = (games || []).some(g => {
            const start = new Date(g.commence_time);
            const now = new Date();
            return start <= now && (now - start) < 4 * 60 * 60 * 1000;
        });
        if (!anyInProgress) return;
        const liveScoresInterval = setInterval(fetchLiveScores, 60 * 1000);
        return () => clearInterval(liveScoresInterval);
    }, [games]);

    // Public betting consensus — admin-only, or any donor who meets the
    // configured threshold (not just Sunday games — fetch one request per
    // distinct ET game date present in the week's slate).
    useEffect(() => {
        if (!canViewPublicBetting || !games?.length) return;
        const dates = [...new Set(games.map(g =>
            new Date(g.commence_time).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
        ))];
        let cancelled = false;
        Promise.all(dates.map(d => publicBettingService.getConsensus(d))).then(results => {
            if (cancelled) return;
            setPublicBetting(results.flat());
        });
        return () => { cancelled = true; };
    }, [canViewPublicBetting, games]);

    const fetchPicks = async () => {
        try {
            const response = await pickService.getWeeklyPicks(user.name);

            if (response.data != null) {
                setSelectedPicks(response.data);
                setTempPicks(response.data);
            }
        } catch (error) {
            setError('Error fetching picks');
        }
    };

    const submitPick = async (data) => {
        const { gameId, homeTeam, awayTeam, type, value, text } = data;
        try {
            const username = user.name;
            const data = { username, homeTeam, awayTeam, type, gameId, value, text }
            await pickService.submitPick(data);
        } catch (error) {
            const msg = error?.response?.data?.error || 'Error submitting pick';
            setError(msg);
            throw error;
        }
    }
    const submitPicks = async () => {
        const { additions, removals } = getPickChanges(selectedPicks, tempPicks);
        try {
            for (let pick of removals) {
                await pickService.removePick({ username: user.name, pickType: pick.type, gameId: pick.gameId, text: pick.text });
            }
            for (let pick of additions) {
                await submitPick(pick);
            }
            await fetchPicks();
            setShowConfirm(false);
            setMessage("Successfully Submitted Picks");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (_) {
            setShowConfirm(false);
        }
    }

    const isOpposite = (type) => {
        switch (type) {
            case "dog":
                return "favorite"
            case "favorite":
                return "dog"
            case "over":
                return "under"
            case "under":
                return "over"
            default:
                return false
        }
    }

    const updatePick = (gameId, homeTeam, awayTeam, type, value, text, commenceTime) => {
        if (gameStarted(commenceTime)) {
            setError("Game Already Started");
            return;
        }

        const existingPick = tempPicks.find(pick => pick.type === type);
        if (existingPick) {
            const existingPickCommenceTime = getCommenceTimeByGameId(existingPick.gameId);
            if (existingPickCommenceTime && gameStarted(existingPickCommenceTime)) {
                setError(`You already selected a ${type} in a game that has started`);
                return;
            }
        }

        if (type === "gotw") {
            setTempPicks(prevState => {
                const existingIndex = prevState.findIndex(pick => pick.type === "gotw");
                if (existingIndex !== -1 && prevState[existingIndex].text === text) {
                    return prevState.filter((_, i) => i !== existingIndex);
                }
                if (existingIndex !== -1) {
                    const newState = [...prevState];
                    newState[existingIndex] = { gameId, homeTeam, awayTeam, type, value, text };
                    return newState;
                }
                return [...prevState, { gameId, homeTeam, awayTeam, type, value, text }];
            });
            return;
        }

        const oppositePick = tempPicks.find(pick =>
            pick.type === isOpposite(type) &&
            pick.gameId === gameId
        )
        if (oppositePick) {
            setTempPicks(prevState => {
                // Remove the opposite pick and upsert the new pick in one update
                const withoutOpposite = prevState.filter(
                    pick => !(pick.type === isOpposite(type) && pick.gameId === gameId)
                );
                const existingIndex = withoutOpposite.findIndex(pick => pick.type === type);
                if (existingIndex !== -1) {
                    const newState = [...withoutOpposite];
                    newState[existingIndex] = { gameId, homeTeam, awayTeam, type, value, text };
                    return newState;
                }
                return [...withoutOpposite, { gameId, homeTeam, awayTeam, type, value, text }];
            });
            return;
        }

        setTempPicks(prevState => {
            const existingIndex = prevState.findIndex(
                pick => pick.type === type
            );

            if (existingIndex !== -1 && prevState[existingIndex].gameId === gameId) {
                const newState = [...prevState];
                newState.splice(existingIndex, 1);
                return newState;
            }

            if (existingIndex !== -1) {
                const newState = [...prevState];
                newState[existingIndex] = { gameId, homeTeam, awayTeam, type, value, text };
                return newState;
            }

            return [...prevState, { gameId, homeTeam, awayTeam, type, value, text }];
        });
    };
    const gotwGameId = games.length > 0
        ? (games.find(g => g.isGotw)?.gameId ?? [...games].sort((a, b) => new Date(b["commence_time"]) - new Date(a["commence_time"]))[0]["gameId"])
        : null;

    const displayGames = games;

    // Build the ordered list: GOTW game first (as gotw), then all games in chronological order (GOTW game included as regular)
    // Games that have started AND the user has a pick on are floated to the top (after GOTW)
    const pickedGameIds = new Set(tempPicks.map(p => p.gameId));
    const sortedGames = [...displayGames].sort((a, b) => {
        const aStarted = gameStarted(a.commence_time);
        const bStarted = gameStarted(b.commence_time);
        const aPicked = pickedGameIds.has(a.gameId);
        const bPicked = pickedGameIds.has(b.gameId);
        // Float: started + picked comes first
        const aPriority = aStarted && aPicked;
        const bPriority = bStarted && bPicked;
        if (aPriority && !bPriority) return -1;
        if (!aPriority && bPriority) return 1;
        return new Date(a["commence_time"]) - new Date(b["commence_time"]);
    });
    const gotwGame = displayGames.find(g => g["gameId"] === gotwGameId);
    const gameEntries = gotwGame
        ? [{ game: gotwGame, isGotw: true }, ...sortedGames.map(g => ({ game: g, isGotw: false }))]
        : sortedGames.map(g => ({ game: g, isGotw: false }));

    const pendingCount = getPickChanges(selectedPicks, tempPicks).count;
    const pickedCount = tempPicks.length;

    const pickTypeLabel = (type) => {
        switch (type) {
            case "favorite": return "Favorite";
            case "dog": return "Underdog";
            case "over": return "Over";
            case "under": return "Under";
            case "gotw": return "Game of the Week";
            default: return type;
        }
    };

    return (
        <>
            <StevenGameInfo
                showStevenInfo={showStevenInfo}
                selectedGameId={selectedGameId}
                setShowStevenInfo={setShowStevenInfo}
                homeTeam={selectedGame?.home}
                awayTeam={selectedGame?.away}
                homeSpread={selectedGame?.homeSpread}
                awaySpread={selectedGame?.awaySpread}
                over={selectedGame?.over}
                canViewPublicBetting={canViewPublicBetting}
                publicBetting={publicBetting}
            />

            {/* Confirmation Modal */}
            <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Your Picks</DialogTitle>
                <DialogContent dividers>
                    {tempPicks.length === 0 ? (
                        <Typography color="text.secondary">No picks selected.</Typography>
                    ) : (
                        <List disablePadding>
                            {["favorite", "dog", "over", "under", "gotw"]
                                .filter((type) => tempPicks.some(p => p.type === type))
                                .map((type) => {
                                    const pick = tempPicks.find(p => p.type === type);
                                    const isTotal = type === "over" || type === "under" ||
                                        (type === "gotw" && pick?.text?.includes("Over") || type === "gotw" && pick?.text?.includes("Under"));
                                    const homeLogoName = pick ? `logos/${pick.homeTeam?.split(" ").pop()}.png` : null;
                                    const awayLogoName = pick ? `logos/${pick.awayTeam?.split(" ").pop()}.png` : null;
                                    // For spread picks, determine which team was picked from the text
                                    const pickedTeamLogo = pick && !isTotal
                                        ? (() => {
                                            const homeLast = pick.homeTeam?.split(" ").pop();
                                            const awayLast = pick.awayTeam?.split(" ").pop();
                                            if (pick.text?.includes(pick.homeTeam)) return `logos/${homeLast}.png`;
                                            if (pick.text?.includes(pick.awayTeam)) return `logos/${awayLast}.png`;
                                            return null;
                                        })()
                                        : null;
                                    return (
                                        <ListItem key={type} disablePadding sx={{ py: 0.75, gap: 1.5, alignItems: "center" }}>
                                            <Box sx={{ width: 64, flexShrink: 0, display: "flex", gap: 0.5, justifyContent: "center", alignItems: "center" }}>
                                                {pick && isTotal ? (
                                                    <>
                                                        <img src={awayLogoName} alt="" width={28} height={28} style={{ objectFit: "contain" }} />
                                                        <img src={homeLogoName} alt="" width={28} height={28} style={{ objectFit: "contain" }} />
                                                    </>
                                                ) : pick && pickedTeamLogo ? (
                                                    <img src={pickedTeamLogo} alt="" width={32} height={32} style={{ objectFit: "contain" }} />
                                                ) : null}
                                            </Box>
                                            <ListItemText
                                                primary={pickTypeLabel(type)}
                                                secondary={pick ? pick.text : <em style={{ color: "#888" }}>No pick</em>}
                                                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                                                secondaryTypographyProps={{ fontSize: 13 }}
                                            />
                                        </ListItem>
                                    );
                                })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions sx={{ gap: 1, px: 2, pb: 2 }}>
                    <StevenButton onClick={() => setShowConfirm(false)} sx={{ color: "gray", borderColor: "gray" }} variant="outlined">Cancel</StevenButton>
                    <StevenButton onClick={submitPicks}>Confirm</StevenButton>
                </DialogActions>
            </Dialog>

            <Box id="game-picks-form" sx={{ maxWidth: 600, mx: "auto" }}>
                {gameEntries
                    .map(({ game, isGotw }, entryIndex) => {
                        let home_team = game["home_team"];
                        let away_team = game["away_team"];
                        let home_spread = game["home_spread"]
                        let away_spread = game["away_spread"]

                        let home_team_name = home_team.split(" ").pop()
                        let away_team_name = away_team.split(" ").pop()

                        let home_logo = `logos/${home_team_name}.png`;
                        let away_logo = `logos/${away_team_name}.png`;

                        let over = game["over"]
                        let under = game["under"]
                        let commenceTime = game["commence_time"]
                        let home_spread_num = +home_spread
                        let away_spread_num = +away_spread
                        let favorite = home_spread_num > away_spread_num ? away_team + " " + away_spread_num : home_team + " " + home_spread_num
                        let underdog = home_spread_num > away_spread_num ? home_team + " +" + home_spread_num : away_team + " +" + away_spread_num
                        let favorite_spread = home_spread_num > away_spread_num ? away_spread_num : home_spread_num
                        let underdog_spread = home_spread_num > away_spread_num ? home_spread_num : away_spread_num

                        const gameIdStr = game["gameId"];
                        const away_picked = Array.isArray(tempPicks)
                            ? isGotw
                                ? tempPicks.find(obj => obj.type === "gotw" && !obj.text.includes("Over") && !obj.text.includes("Under") && obj.text.includes(away_team_name))
                                : tempPicks.find(obj => (obj.gameId === gameIdStr || (obj.text?.includes(away_team) && obj.text?.includes(home_team))) && obj.type === "dog" && obj.text.includes(away_team_name)) ||
                                tempPicks.find(obj => (obj.gameId === gameIdStr || (obj.text?.includes(away_team) && obj.text?.includes(home_team))) && obj.type === "favorite" && obj.text.includes(away_team_name))
                            : null;
                        const home_picked = Array.isArray(tempPicks)
                            ? isGotw
                                ? tempPicks.find(obj => obj.type === "gotw" && !obj.text.includes("Over") && !obj.text.includes("Under") && obj.text.includes(home_team_name))
                                : tempPicks.find(obj => (obj.gameId === gameIdStr || (obj.text?.includes(away_team) && obj.text?.includes(home_team))) && obj.type === "dog" && obj.text.includes(home_team_name)) ||
                                tempPicks.find(obj => (obj.gameId === gameIdStr || (obj.text?.includes(away_team) && obj.text?.includes(home_team))) && obj.type === "favorite" && obj.text.includes(home_team_name))
                            : null;
                        const over_picked = Array.isArray(tempPicks)
                            ? tempPicks.find(obj => obj.type === "over" && obj.text?.includes(home_team) && obj.text?.includes(away_team))
                            : null;
                        const under_picked = Array.isArray(tempPicks)
                            ? tempPicks.find(obj => obj.type === "under" && obj.text?.includes(home_team) && obj.text?.includes(away_team))
                            : null;

                        const dateObj = new Date(commenceTime);
                        const isLocked = gameStarted(commenceTime);
                        const liveScore = liveScores[`${away_team}@${home_team}`];
                        const hasScore = liveScore && liveScore.state !== 'pre';
                        const isCompleted = liveScore?.is_completed ?? false;

                        const getTeamPickClass = (pick) => {
                            if (!pick) return "";
                            if (isCompleted) {
                                if (pick.result === 1) return "pick-win";
                                if (pick.result === -1) return "pick-loss";
                                if (pick.result === 0) return "pick-push";
                            }
                            return isGotw ? "gotw-picked" : "picked";
                        };

                        const getTotalPickClass = (pick) => {
                            if (!pick) return null;
                            if (isCompleted) {
                                if (pick.result === 1) return "total-win";
                                if (pick.result === -1) return "total-loss";
                                if (pick.result === 0) return "total-push";
                            }
                            return isGotw ? "gotw-total-picked" : "total-picked";
                        };

                        // Win/loss/push icon so graded picks aren't shown by color alone.
                        const resultBadge = (pick, style) => isCompleted && pick
                            ? <ResultIcon result={pick.result} size={16} style={{ position: "absolute", ...style }} />
                            : null;
                        const totalResultBadgeStyle = { top: -6, right: -6, backgroundColor: "#1e1e1e", borderRadius: "50%" };

                        const awayPickClass = getTeamPickClass(away_picked);
                        const homePickClass = getTeamPickClass(home_picked);

                        const gotwOver = isGotw ? tempPicks.find(obj => obj.type === "gotw" && obj.text.includes("Over")) : null;
                        const gotwUnder = isGotw ? tempPicks.find(obj => obj.type === "gotw" && obj.text.includes("Under")) : null;
                        const overPickForClass = isGotw ? gotwOver : over_picked;
                        const underPickForClass = isGotw ? gotwUnder : under_picked;
                        const overPickClass = getTotalPickClass(overPickForClass);
                        const underPickClass = getTotalPickClass(underPickForClass);

                        // Detect when a stored pick's locked-in line no longer matches the
                        // game's current line (odds moved since the pick was made).
                        const lineHasMoved = (pick, currentValue) => {
                            if (!pick) return false;
                            const picked = parseFloat(pick.value);
                            const current = parseFloat(currentValue);
                            return !isNaN(picked) && !isNaN(current) && picked !== current;
                        };
                        const awayLineMoved = lineHasMoved(away_picked, away_spread_num);
                        const homeLineMoved = lineHasMoved(home_picked, home_spread_num);
                        const overLineMoved = lineHasMoved(overPickForClass, over);
                        const underLineMoved = lineHasMoved(underPickForClass, under);
                        return (
                            <Paper
                                key={isGotw ? `gotw-${game["gameId"]}` : game["gameId"]}
                                className={isLocked ? "locked-card" : undefined}
                                sx={{
                                    mb: 2.5,
                                    ...(isGotw && {
                                        border: `2px solid ${colors.gold}`,
                                        boxShadow: "0 2px 16px rgba(212, 175, 55, 0.35)",
                                    })
                                }}
                                elevation={isGotw ? 4 : 2}
                            >
                                {isGotw && (
                                    <Box sx={{
                                        background: `linear-gradient(90deg, #9a7c0a 0%, ${colors.gold} 40%, #F5CB5C 70%, ${colors.gold} 100%)`,
                                        color: "#3d2b00",
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: "2px 2px 0 0",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.5,
                                    }}>
                                        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: "0.8rem", letterSpacing: "0.04em" }}>
                                            GAME OF THE WEEK
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.5, pt: 1.5 }}>
                                    <Typography variant="body2" fontWeight="bold">
                                        {hasScore
                                            ? (liveScore.is_completed ? "Final" : liveScore.status_short_detail)
                                            : `${dateObj.toLocaleDateString('en-US', { weekday: 'short' })}, ${dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        {isLocked && (
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.disabled", display: "flex", alignItems: "center", gap: 0.5 }}
                                            >
                                                <BsLockFill aria-hidden="true" /> Locked
                                            </Typography>
                                        )}
                                        <MuiLink
                                            component="button"
                                            type="button"
                                            variant="body2"
                                            underline="always"
                                            color="text.secondary"
                                            onClick={() => {
                                                setShowStevenInfo(true);
                                                setSelectedGameId(game["gameId"]);
                                                setSelectedGame({ home: home_team, away: away_team, homeSpread: home_spread, awaySpread: away_spread, over });
                                            }}
                                            sx={{ fontSize: "0.9rem", px: 1, py: 1.25, my: -1.25, mr: -1 }}
                                        >
                                            Game Info
                                        </MuiLink>
                                    </Box>
                                </Box>
                                {hasScore && (() => {
                                    const awayLs = liveScore.away_linescores || [];
                                    const homeLs = liveScore.home_linescores || [];
                                    const maxPeriod = Math.max(4, ...[...awayLs, ...homeLs].map(q => q.period));
                                    const periods = Array.from({ length: maxPeriod }, (_, i) => i + 1);
                                    const colLabel = (p) => p <= 4 ? `Q${p}` : `OT${p - 4}`;
                                    const getVal = (ls, p) => { const q = ls.find(x => x.period === p); return q ? q.displayValue : '-'; };
                                    const cellSx = { fontSize: "0.75rem", textAlign: "center", px: 0.75, py: 0.25, minWidth: 28 };
                                    const headerSx = { ...cellSx, color: "text.secondary", fontWeight: 600 };
                                    const totalSx = { ...cellSx, fontWeight: 700, fontSize: "0.8rem", borderLeft: "1px solid rgba(255,255,255,0.12)" };
                                    const nameSx = { fontSize: "0.75rem", fontWeight: 600, textAlign: "left", pr: 1, minWidth: 32 };
                                    return (
                                        <Box sx={{ overflowX: "auto", px: 1.5, pt: 1.5, pb: 0.5 }}>
                                            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                                                <Box component="thead">
                                                    <Box component="tr">
                                                        <Box component="th" sx={nameSx} />
                                                        {periods.map(p => (
                                                            <Box component="th" key={p} sx={headerSx}>{colLabel(p)}</Box>
                                                        ))}
                                                        <Box component="th" sx={{ ...headerSx, borderLeft: "1px solid rgba(255,255,255,0.12)", fontWeight: 700 }}>T</Box>
                                                    </Box>
                                                </Box>
                                                <Box component="tbody">
                                                    {[
                                                        { label: away_team_name, ls: awayLs, total: liveScore.away_score },
                                                        { label: home_team_name, ls: homeLs, total: liveScore.home_score },
                                                    ].map(({ label, ls, total }) => (
                                                        <Box component="tr" key={label}>
                                                            <Box component="td" sx={nameSx}>{label}</Box>
                                                            {periods.map(p => (
                                                                <Box component="td" key={p} sx={cellSx}>{getVal(ls, p)}</Box>
                                                            ))}
                                                            <Box component="td" sx={totalSx}>{total ?? '-'}</Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })()}
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center", width: "100%" }}>
                                    <div className={`team-container ${awayPickClass}`} onClick={() =>
                                        updatePick(
                                            game["gameId"],
                                            home_team,
                                            away_team,
                                            isGotw ? "gotw" : (away_spread_num > 0 ? "dog" : "favorite"),
                                            away_spread_num > 0 ? underdog_spread : favorite_spread,
                                            away_spread_num > 0 ? underdog : favorite,
                                            commenceTime
                                        )
                                    }>
                                        {resultBadge(away_picked, { top: 8, right: 10 })}
                                        <img src={away_logo} alt={away_team} className="logo" />
                                        <div className="team-record">{records[away_team] ?? ""}</div>
                                        <div><div className="team-name">{away_team}</div><b>{away_spread_num > 0 ? "+" + away_spread_num : away_spread_num}</b></div>
                                        {awayLineMoved && (
                                            <div className="line-moved-note" title={`Line moved since you picked ${formatLineValue(away_picked.value)} — current line is ${formatLineValue(away_spread_num)}`}>
                                                Line moved · you have {formatLineValue(away_picked.value)}
                                            </div>
                                        )}
                                    </div>
                                    <div className={`team-container ${homePickClass}`} onClick={() =>
                                        updatePick(
                                            game["gameId"],
                                            home_team,
                                            away_team,
                                            isGotw ? "gotw" : (away_spread_num > 0 ? "favorite" : "dog"),
                                            away_spread_num > 0 ? favorite_spread : underdog_spread,
                                            away_spread_num > 0 ? favorite : underdog,
                                            commenceTime
                                        )
                                    }>
                                        {resultBadge(home_picked, { top: 8, right: 10 })}
                                        <img src={home_logo} alt={home_team} className="logo" />
                                        <div className="team-record">{records[home_team] ?? ""}</div>
                                        <div><div className="team-name">{home_team}</div><b>{home_spread_num > 0 ? "+" + home_spread_num : home_spread_num}</b></div>
                                        {homeLineMoved && (
                                            <div className="line-moved-note" title={`Line moved since you picked ${formatLineValue(home_picked.value)} — current line is ${formatLineValue(home_spread_num)}`}>
                                                Line moved · you have {formatLineValue(home_picked.value)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="icon-text-container">
                                        {overPickClass ? (
                                            <span className={overPickClass} style={{ position: "relative" }}>
                                                {resultBadge(overPickForClass, totalResultBadgeStyle)}
                                                <h2 className="bi bi-arrow-up-square-fill" onClick={() =>
                                                    updatePick(game["gameId"], home_team, away_team, isGotw ? "gotw" : "over", over, `${home_team} ${away_team} Over ${over}`, commenceTime)
                                                }></h2>
                                            </span>
                                        ) : (
                                            <h2 className="total bi bi-arrow-up-square-fill" onClick={() =>
                                                updatePick(game["gameId"], home_team, away_team, isGotw ? "gotw" : "over", over, `${home_team} ${away_team} Over ${over}`, commenceTime)
                                            }></h2>
                                        )}
                                        <div className="over-text"><b>{over}</b></div>
                                        {underPickClass ? (
                                            <span className={underPickClass} style={{ position: "relative" }}>
                                                {resultBadge(underPickForClass, totalResultBadgeStyle)}
                                                <h2 className="bi bi-arrow-down-square-fill" onClick={() =>
                                                    updatePick(game["gameId"], home_team, away_team, isGotw ? "gotw" : "under", under, `${home_team} ${away_team} Under ${under}`, commenceTime)
                                                }></h2>
                                            </span>
                                        ) : (
                                            <h2 className="total bi bi-arrow-down-square-fill" onClick={() =>
                                                updatePick(game["gameId"], home_team, away_team, isGotw ? "gotw" : "under", under, `${home_team} ${away_team} Under ${under}`, commenceTime)
                                            }></h2>
                                        )}
                                        {(overLineMoved || underLineMoved) && (
                                            <div
                                                className="line-moved-note"
                                                title={`Line moved since you picked ${formatLineValue(overLineMoved ? overPickForClass.value : underPickForClass.value)} — current total is ${formatLineValue(overLineMoved ? over : under)}`}
                                            >
                                                Line moved · you have {formatLineValue(overLineMoved ? overPickForClass.value : underPickForClass.value)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Paper>
                        );
                    })}
                <Box sx={{ pb: 10 }} />
            </Box>
            <Box sx={{
                position: "fixed",
                // Sit above the mobile tab bar and the iPhone home indicator.
                bottom: {
                    xs: `calc(${MOBILE_TAB_BAR_HEIGHT + 12}px + env(safe-area-inset-bottom))`,
                    md: "calc(20px + env(safe-area-inset-bottom))",
                },
                left: 0,
                right: 0,
                px: 2,
                zIndex: 1000,
                maxWidth: 600,
                mx: "auto",
                boxSizing: "border-box",
            }}>
                <StevenButton
                    onClick={() => setShowConfirm(true)}
                    disabled={pendingCount === 0}
                    sx={{
                        width: "100%",
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "bold",
                        borderRadius: "50px",
                        backgroundColor: "white",
                        color: "black",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        "&:hover": {
                            backgroundColor: "#f0f0f0",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#3a3a3a",
                            color: "rgba(255,255,255,0.6)",
                        },
                        transition: "all 0.2s ease-in-out",
                    }}
                >
                    {pendingCount === 0
                        ? `${pickedCount} of 5 picks saved`
                        : `Submit ${pendingCount} ${pendingCount === 1 ? "change" : "changes"} (${pickedCount} of 5 picked)`}
                </StevenButton>
            </Box>
        </>
    );
};

export default StevenGameList;
